import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Locale } from "@/i18n/locale";

export type NewsItem = {
  id: string;
  source_url: string;
  source_name: string;
  title: string;
  summary: string | null;
  topic: string;
  published_at: string | null;
  image_url: string | null;
  affiliate_url: string | null;
  affiliate_network: string | null;
};

const COLS =
  "id, source_url, source_name, title, summary, topic, published_at, image_url, affiliate_url, affiliate_network";

export function useNewsItems(limit = 20, lang: Locale = "en") {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("news_items")
          .select(COLS)
          .eq("is_active", true)
          .eq("lang", lang)
          .order("published_at", { ascending: false, nullsFirst: false })
          .limit(limit);
        if (!alive) return;
        if (error) throw error;
        setItems((data ?? []) as NewsItem[]);
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : "Failed to load news");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [limit, lang]);

  return { items, loading, error };
}
